import React, { useState } from "react";
import { API } from "./../../config/api";
import { codes } from "country-calling-code";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export default function Register({ isOpen, onClose, openLogin }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    country: "India",
    country_code: "91",
    city: "",
    state: "",
    role: "user",
    account_type: "customer",
  });
  const [providerOptions, setProviderOptions] = useState(["visitor"]);

  // Validation states
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to get display phone number (for preview only)
  const getDisplayPhoneNumber = () => {
    return `${formData.country_code} ${formData.phone}`;
  };

  // Helper function to validate phone number
  const validatePhoneNumber = () => {
    const phone = formData.phone;

    // 🔥 Strict India validation (10 digits starting 6-9)
    if (formData.country_code === "91") {
      return /^[6-9]\d{9}$/.test(phone);
    }

    // 🌍 Fallback to library for other countries
    try {
      const fullPhone = `+${formData.country_code}${phone}`;
      const phoneNumber = parsePhoneNumberFromString(fullPhone);
      return phoneNumber && phoneNumber.isValid();
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (formData.name.length > 50) {
      newErrors.name = "Name must be less than 50 characters";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhoneNumber()) {
      const code = formData.country_code;
      if (code === "91") {
        newErrors.phone =
          "Enter valid Indian number (10 digits starting with 6-9)";
      } else if (code === "1") {
        newErrors.phone = "Enter valid US number (10 digits)";
      } else {
        newErrors.phone = "Enter valid international number";
      }
    }

    // Provider validation
    if (formData.account_type === "provider") {
      if (!providerOptions || providerOptions.length === 0) {
        newErrors.providerOptions =
          "Please select at least one: Machines / Designers / Workspace";
      }
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    // State validation
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    // Email validation (OPTIONAL)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Update phone input handler to store only digits
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, ""); // Only digits
    handleChange({ target: { name: "phone", value } });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // ✅ Always send provider_options - even for customers
      let providerOptionsToSend = providerOptions.filter(
        (opt) => opt !== "visitor",
      );

      const payload = {
        ...formData,
        country_code: formData.country_code, // "91"
        phone: formData.phone.trim(), // "8989493929"
        provider_options: providerOptionsToSend, // Always send array
      };

      console.log("Sending payload:", payload); // Debug log
      const res = await fetch(API.REGISTER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        alert("Account created successfully! Please login.");

        // Reset form
        setFormData({
          name: "",
          email: "",
          password: "",
          phone: "",
          country: "India",
          country_code: "91", // Reset to India
          city: "",
          state: "",
          role: "user",
          account_type: "customer",
        });
        setProviderOptions(["visitor"]);
        setErrors({});
        openLogin();
        onClose();
      } else {
        if (data.message === "Phone number already registered") {
          setErrors((prev) => ({
            ...prev,
            phone: "This phone number is already registered",
          }));
        } else if (data.message === "Email already exists") {
          setErrors((prev) => ({
            ...prev,
            email: "This email is already registered",
          }));
        } else {
          alert(data.message || "Registration failed");
        }
      }
      // ... rest of the code remains same
    } catch (error) {
      console.error("Registration error:", error);
      alert("Server error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProviderChange = (e) => {
    const { value, checked } = e.target;

    let updatedOptions = [...providerOptions];

    if (value === "visitor") {
      if (checked) {
        updatedOptions = ["visitor"];
      } else {
        updatedOptions = [];
      }
    } else {
      if (checked) {
        updatedOptions = updatedOptions.filter((opt) => opt !== "visitor");
        if (!updatedOptions.includes(value)) {
          updatedOptions.push(value);
        }
      } else {
        updatedOptions = updatedOptions.filter((opt) => opt !== value);
      }

      if (updatedOptions.length === 0) {
        updatedOptions = ["visitor"];
      }
    }

    setProviderOptions(updatedOptions);

    setFormData((prev) => ({
      ...prev,
      account_type: updatedOptions.includes("visitor")
        ? "customer"
        : "provider",
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="register-popup-overlay" onClick={onClose}>
      <div className="register-popup-card" onClick={(e) => e.stopPropagation()}>
        {/* CLOSE BUTTON */}
        <button className="register-popup-close" onClick={onClose}>
          ✕
        </button>

        {/* HEADER */}
        <div className="register-popup-header">
          <h2>Create Account</h2>
          <p>Join and manage your services easily</p>
        </div>

        {/* FORM */}
        <form
          className="register-popup-form"
          onSubmit={handleSubmit}
          noValidate
        >
          {/* NAME */}
          <div className="register-popup-group">
            <input
              name="name"
              placeholder="Full Name"
              className={`register-popup-input ${
                errors.name ? "register-popup-error-field" : ""
              }`}
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && (
              <span className="register-popup-error">{errors.name}</span>
            )}
          </div>

          {/* PHONE + COUNTRY CODE */}
          <div className="register-popup-group">
            <div className="register-popup-grid">
              <select
                name="country_code"
                className="register-popup-select"
                value={formData.country_code}
                onChange={handleChange}
              >
                {codes.map((country, index) => {
                  const code = country.countryCodes[0];

                  return (
                    <option key={index} value={code}>
                      +{code} {country.country}
                    </option>
                  );
                })}
              </select>

              <input
                name="phone"
                placeholder="Mobile Number"
                className={`register-popup-input ${
                  errors.phone ? "register-popup-error-field" : ""
                }`}
                value={formData.phone}
                onChange={handlePhoneChange}
                maxLength="15"
              />
            </div>

            {/* {formData.phone && (
          <div className="register-popup-phone-preview">
            Full number: <strong>+{getDisplayPhoneNumber()}</strong>
          </div>
        )} */}

            {errors.phone && (
              <span className="register-popup-error">{errors.phone}</span>
            )}
          </div>

          {/* CITY + STATE */}
          <div className="register-popup-city-grid">
            <div className="register-popup-group">
              <input
                name="city"
                placeholder="City"
                className={`register-popup-input ${
                  errors.city ? "register-popup-error-field" : ""
                }`}
                value={formData.city}
                onChange={handleChange}
              />
              {errors.city && (
                <span className="register-popup-error">{errors.city}</span>
              )}
            </div>

            <div className="register-popup-group">
              <input
                name="state"
                placeholder="State"
                className={`register-popup-input ${
                  errors.state ? "register-popup-error-field" : ""
                }`}
                value={formData.state}
                onChange={handleChange}
              />
              {errors.state && (
                <span className="register-popup-error">{errors.state}</span>
              )}
            </div>

            <div className="register-popup-group">
              <input
                name="country"
                placeholder="Country"
                className={`register-popup-input ${
                  errors.country ? "register-popup-error-field" : ""
                }`}
                value={formData.country}
                onChange={handleChange}
              />
              {errors.country && (
                <span className="register-popup-error">{errors.country}</span>
              )}
            </div>
          </div>

          {/* PROVIDER OPTIONS */}
          <div className="register-popup-provider-box">
            <h4 className="register-popup-provider-title">
              What do you provide?
            </h4>

            <div className="register-popup-provider-grid">
              {[
                { value: "visitor", label: "I am Visitor" },
                { value: "machines", label: "I want to provide Machines" },
                { value: "designers", label: "I am Designer" },
                {
                  value: "workspaces",
                  label: "I want to give Workspace on Rent",
                },
              ].map((opt) => (
                <label key={opt.value} className="register-popup-checkbox">
                  <input
                    type="checkbox"
                    value={opt.value}
                    checked={providerOptions.includes(opt.value)}
                    onChange={handleProviderChange}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>

            {errors.providerOptions && (
              <span className="register-popup-error">
                {errors.providerOptions}
              </span>
            )}
          </div>

          {/* EMAIL */}
          <div className="register-popup-group">
            <input
              name="email"
              type="email"
              placeholder="Email Address (Optional)"
              className={`register-popup-input ${
                errors.email ? "register-popup-error-field" : ""
              }`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <span className="register-popup-error">{errors.email}</span>
            )}
          </div>

          {/* PASSWORD */}
          <div className="register-popup-group">
            <input
              name="password"
              type="password"
              placeholder="Password (min 6 chars)"
              className={`register-popup-input ${
                errors.password ? "register-popup-error-field" : ""
              }`}
              value={formData.password}
              onChange={handleChange}
            />
            {errors.password && (
              <span className="register-popup-error">{errors.password}</span>
            )}
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            className="register-popup-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing..." : "Create Account"}
          </button>
        </form>

        {/* FOOTER */}
        <div className="register-popup-footer">
          Already have an account?{" "}
          <span
            className="register-popup-link"
            onClick={() => {
              onClose();
              openLogin();
            }}
          >
            Login here
          </span>
        </div>
      </div>
    </div>
  );
}
